import React, { useContext, useState, useMemo, useEffect } from "react";
import DateArea from "./DateArea";
import { Box } from "@mui/material";
import DataBlock from "./DataBlock";
import CommunityAnalysisContext from "features/metadesk/CommunityAnalysis/contexts/CommunityAnalysis.context";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { StyledWhiteContainer } from "features/metadesk/CommunityAnalysis/CommunityAnalysis.styles";
import ChartContainer from "./chart/ChartContainer";
import { useTheme } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AutoCompleteDropdown,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import { isEmpty, get } from "lodash-es";
import useCommunity from "../hooks/useCommunityState.hooks";
import { useDebounceEffect } from "components/ConnectWallet/ConnectWallet";
import HeatContainerForOverView from "./HeatContainerForOverView";
import moment from "moment";
import LoadingComponent from "components/LoadingComponent/LoadingComponent";
import { useSelector } from "react-redux";
import { selectServiceModule } from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { serviceColors } from "features/configure/chartColors";
import EmptyImgStatus from "components/EmptyState/EmptyImageStatus";
import useNavigateEntity from "hooks/useNavigateEntity";
import { StyledSkeleton } from "components/LoadingComponent/LoadingComponent.styles";
import LoadingFilter from "components/LoadingComponent/LoadingFilter";
import { useParams } from "react-router";

const OverviewContainer = () => {
  const serviceModule = useSelector(selectServiceModule);
  const isDiscordConnected = get(serviceModule, "discordAnalytics.connected");
  const isTelegramConnected = get(serviceModule, "telegramAnalytics.connected");
  const { subEntityName } = useParams();
  const isConnected =
    subEntityName || (isDiscordConnected && isTelegramConnected);
  const { navigateEntity } = useNavigateEntity();
  const colors = serviceColors.small;
  const [discordColor, telegramColor] = [colors[0], colors[1]];

  const { formatMessage } = useIntl();
  const {
    highLevelDataArray,
    basicData,
    basicDataLoading,
    overViewParticipantsData,
    overViewParticipantsDataLoading,
    engagementGrowthData,
    engagementGrowthDataLoading,
    overViewKeywordCloudData,
    overViewKeywordCloudDataLoading,
    fetchBasicData,
    fetchNewParticipantsData,
    fetchEngagementsGrowthData,
    fetchOverViewKeywordCloudData,
    overViewKeywordTrendData,
    overViewKeywordTrendDataLoading,
    fetchOverViewKeywordTrend,
  } = useCommunity();

  const timeFrameOptions = [
    {
      label: formatMessage({ id: "dropdown.timeInterval.daily" }),
      value: "days",
    },
    {
      label: formatMessage({ id: "dropdown.timeInterval.weekly" }),
      value: "weeks",
    },
    {
      label: formatMessage({ id: "dropdown.timeInterval.monthly" }),
      value: "months",
    },
  ];

  const hasAnyIsLoading =
    basicDataLoading ||
    overViewParticipantsDataLoading ||
    engagementGrowthDataLoading ||
    overViewKeywordCloudDataLoading ||
    overViewKeywordTrendDataLoading;

  const { timeFrame, setTimeFrame, dateRange } = useContext(
    CommunityAnalysisContext
  );

  const targetDataArray = [
    {
      type: "Discord",
      name: basicData.serverName,
      icon: (
        <Box
          component="i"
          color={"#5865F2"}
          className="meta-crm-icon-ic_discord font-size-18"
        />
      ),
    },
    {
      type: "Telegram",
      name: basicData.groupName,
      icon: (
        <Box
          component="i"
          color={telegramColor}
          className="meta-crm-icon-ic_telegram font-size-12"
        />
      ),
    },
  ];

  const overViewParticipantsDataArray = [
    {
      name: "Discord",
      value: overViewParticipantsData?.discord?.newParticipants,
      rate: overViewParticipantsData?.discord?.growthRate,
      icon: (
        <Box
          component="i"
          color="#5865F2"
          className="meta-crm-icon-ic_discord font-size-18"
        />
      ),
      color: discordColor,
    },
    {
      name: "Telegram",
      value: overViewParticipantsData?.telegram?.newParticipants,
      rate: overViewParticipantsData?.telegram?.growthRate,
      icon: (
        <Box
          component="i"
          color="#25A3E2"
          className="meta-crm-icon-ic_telegram font-size-12"
        />
      ),
      color: telegramColor,
    },
  ];

  const overViewParticipantsDataForChart = {
    dataArray: overViewParticipantsDataArray,
    seriesData: [
      {
        name: "Access From",
        type: "pie",
        radius: ["30%", "60%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 3,
          borderColor: "#fff",
          borderWidth: 2,
        },

        emphasis: {
          label: {
            show: true,
            fontSize: 18,
            fontWeight: "bold",
          },
        },
        label: {
          formatter: "{b}\n{d}%",
          color: "#A5A5A5",
          lineHeight: 16,
        },
        labelLine: {
          length: 0,
          length2: 0,
          show: false,
        },

        data: overViewParticipantsDataArray.map((item) => {
          return {
            name: item.name,
            value: item.value,
            itemStyle: {
              color: item.color,
            },
          };
        }),
      },
    ],
  };

  const overViewParticipantsGrowthSeriesData = useMemo(() => {
    return isEmpty(overViewParticipantsData.trend)
      ? []
      : [
          {
            name: "Discord", // 為 series 添加名稱，供 legend 顯示
            data: overViewParticipantsData?.trend?.map(
              (item) => item.dcNewParticipants
            ),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: discordColor, // 設定柱狀圖的顏色
            },
          },
          {
            name: "Telegram", // 為 series 添加名稱，供 legend 顯示
            data: overViewParticipantsData?.trend?.map(
              (item) => item.tgNewParticipants
            ),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: telegramColor, // 設定柱狀圖的顏色
            },
          },
        ];
  }, [overViewParticipantsData]);

  const overViewParticipantsGrowthForChart = useMemo(() => {
    return {
      showDate: overViewParticipantsData?.trend?.map((item) => item.date),
      seriesData: overViewParticipantsGrowthSeriesData,
    };
  }, [overViewParticipantsData, overViewParticipantsGrowthSeriesData]);

  const engagementGrowthDataSeriesData = useMemo(() => {
    return isEmpty(engagementGrowthData.dailyTrend)
      ? []
      : [
          {
            name: "Discord", // 為 series 添加名稱，供 legend 顯示
            data: engagementGrowthData?.dailyTrend?.map((item) => {
              return { value: item.dc.messages, label: "Messages" };
            }),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: discordColor, // 設定柱狀圖的顏色
            },
            stack: "one",
            label: "Messages",
          },
          {
            name: "Discord", // 為 series 添加名稱，供 legend 顯示
            data: engagementGrowthData?.dailyTrend?.map((item) => {
              return { value: item.dc.reactions, label: "Reactions" };
            }),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: colors[2], // 設定柱狀圖的顏色
            },
            stack: "one",
            label: "Reactions",
          },
          {
            name: "Telegram", // 為 series 添加名稱，供 legend 顯示
            data: engagementGrowthData?.dailyTrend?.map((item) => {
              return { value: item.tg.messages, label: "Messages" };
            }),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: telegramColor, // 設定柱狀圖的顏色
            },
            stack: "two",
            label: "Messages",
          },
          {
            name: "Telegram", // 為 series 添加名稱，供 legend 顯示
            data: engagementGrowthData?.dailyTrend?.map((item) => {
              return { value: item.tg.reactions, label: "Reactions" };
            }),
            type: "bar",
            // barWidth: 15,
            itemStyle: {
              color: colors[3], // 設定柱狀圖的顏色
            },
            stack: "two",
            label: "Reactions",
          },
        ];
  }, [engagementGrowthData]);

  const engagementGrowthDataForChart = useMemo(() => {
    return {
      formatter: function (params) {
        const date = moment(params[0].name).format("DD MMM YYYY"); // 取得X軸的日期

        let result = `<div style="margin-bottom:15px">${date}</div>`;
        params.forEach((param) => {
          result += `${
            param.seriesIndex % 2 === 0
              ? `<span style="color: #A5A5A5">${param.seriesName}</span> <br/>`
              : ""
          } <div style="width:150px; display: flex; align-items: center;">${
            param.marker
          } ${param.data.label}: <span style="margin-left:auto">${
            param.data.value
          }</span></div>`;
        });
        return result;
      },
      showDate: engagementGrowthData?.dailyTrend?.map((item) => item.date),
      seriesData: engagementGrowthDataSeriesData,
    };
  }, [engagementGrowthData, engagementGrowthDataSeriesData]);

  const theme = useTheme();

  const handleChangeTimeFrame = (event, value) => {
    setTimeFrame(value.value);
  };

  const overViewKeywordTrendDataSeriesData = useMemo(() => {
    return isEmpty(overViewKeywordTrendData)
      ? []
      : [
          {
            name: "Discord",
            type: "line",
            areaStyle: {
              color: "rgba(25, 159, 159, 0.4)", // 自訂區域填充顏色
            },
            lineStyle: {
              color: discordColor, // 自訂折線顏色
              width: 2, // 線條寬度
            },
            itemStyle: {
              color: discordColor, // 節點的顏色
              borderColor: discordColor, // 節點的邊框顏色
              borderWidth: 2, // 邊框寬度
            },
            emphasis: {
              focus: "series",
            },
            data: overViewKeywordTrendData.map((item) => item.dc),
          },
          {
            name: "Telegram",
            type: "line",
            areaStyle: {
              color: "rgba(46, 122, 167, 0.4)", // 自訂區域填充顏色
            },
            lineStyle: {
              color: telegramColor, // 自訂折線顏色
              width: 2, // 線條寬度
            },
            itemStyle: {
              color: telegramColor, // 節點的顏色
              borderColor: telegramColor, // 節點的邊框顏色
              borderWidth: 2, // 邊框寬度
            },
            emphasis: {
              focus: "series",
            },
            data: overViewKeywordTrendData.map((item) => item.tg),
          },
        ];
  }, [overViewKeywordTrendData]);

  const overViewKeywordTrendDataForChart = useMemo(() => {
    return {
      showDate: overViewKeywordTrendData.map((item) => item.date),
      seriesData: overViewKeywordTrendDataSeriesData,
    };
  }, [overViewKeywordTrendDataSeriesData, overViewKeywordTrendData]);

  const keywordCloudOptions = useMemo(
    () =>
      overViewKeywordCloudData?.map((item) => ({
        label: item?.value,
        value: item?.keywordIds,
      })),
    [overViewKeywordCloudData]
  );

  const [targetKeyword, setTargetKeyword] = useState({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (!isEmpty(keywordCloudOptions)) {
      setTargetKeyword(keywordCloudOptions[0]);
    } else {
      setTargetKeyword({
        label: "",
        value: "",
      });
    }
  }, [keywordCloudOptions]);

  const handleTargetKeyword = (event, value) => {
    setTargetKeyword(value);
  };

  useDebounceEffect(
    () => {
      if (isConnected) {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        fetchBasicData({ type: "overview", from: startDate, to: endDate });
        fetchNewParticipantsData({ from: startDate, to: endDate });
        fetchEngagementsGrowthData({ from: startDate, to: endDate });
        fetchOverViewKeywordCloudData({ from: startDate, to: endDate });
      }
    },
    50,
    [dateRange, isConnected]
  );

  useDebounceEffect(
    () => {
      if (isConnected) {
        if (targetKeyword.value) {
          fetchOverViewKeywordTrend({
            from: dateRange[0],
            to: dateRange[1],
            keywordIds: targetKeyword.value,
          });
        }
      }
    },
    50,
    [dateRange, targetKeyword.value, isConnected]
  );

  if (!isConnected) {
    return (
      <div className="fade-up-more">
        <DateArea
          sx={{ marginBottom: "8px" }}
          context={CommunityAnalysisContext}
        />
        <EmptyImgStatus
          type="integrate"
          backgroundImg={"Community"}
          description={formatMessage({
            id: "emptyStatus.notIntegrated.description.eachBot",
          })}
          onClick={() => navigateEntity("/settings/general/integration/add")}
        />
      </div>
    );
  }

  return (
    <Box sx={{ position: "relative" }} className="fade-up-more">
      {hasAnyIsLoading && <LoadingFilter />}
      <DateArea
        sx={{ marginBottom: "8px" }}
        context={CommunityAnalysisContext}
      />
      <Box
        sx={{
          padding: "16px",
          background: theme.customColors.white,
          marginBottom: "8px",
        }}
      >
        <Box display={"flex"} alignItems={"flex-start"} mb={1}>
          <Box
            sx={{
              color: theme.customColors.purple[500],
              fontWeight: "700",
              fontSize: "16px",
              whiteSpace: "nowrap",
            }}
          >
            {formatMessage({
              id: "communityAnalysis.overview.subtitle",
            })}
            :
          </Box>
          <Box sx={{ color: theme.customColors.grey[600] }}>
            {formatMessage({
              id: "communityAnalysis.overview.description",
            })}
          </Box>
        </Box>
        <Box display={"flex"} alignItems={"center"}>
          {basicDataLoading ? (
            <StyledSkeleton animation="wave" sx={{ width: "80%" }} />
          ) : (
            <>
              <Box mr="5px">
                {formatMessage({
                  id: "communityAnalysis.instance.labelName",
                })}
                :{" "}
              </Box>

              {targetDataArray.map((target, index) => (
                <Box key={index} display={"flex"} alignItems={"center"}>
                  <Box
                    component="span"
                    marginRight={"5px"}
                    display={"flex"}
                    alignItems={"center"}
                  >
                    {" "}
                    {target.icon}
                  </Box>{" "}
                  {target.name}{" "}
                  {index !== targetDataArray.length - 1 && (
                    <Box ml="3px" mr="5px">
                      ,
                    </Box>
                  )}
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>
      <Box component={Grid} container spacing={1} mb={1}>
        {highLevelDataArray.length > 0
          ? highLevelDataArray.map((highLevelData, index) => (
              <Grid item lg={4} xs={12} key={index}>
                <DataBlock
                  data={highLevelData}
                  isLoading={basicDataLoading}
                ></DataBlock>
              </Grid>
            ))
          : Array.from({ length: 3 }).map((index) => (
              <Grid item lg={4} xs={12} key={index}>
                <DataBlock
                  data={{ isEmpty: true }}
                  isLoading={basicDataLoading}
                ></DataBlock>
              </Grid>
            ))}
      </Box>

      <Box component={Grid} container spacing={1}>
        <Box component={Grid} item xs={12}>
          <StyledWhiteContainer>
            <Box component={Grid} container spacing={1}>
              <Box
                component={Grid}
                item
                xs={12}
                lg={3}
                sx={{
                  position: "relative",
                  "&:after": {
                    content: '""',
                    display: { xs: "none", lg: "block" },
                    width: "1px",
                    height: "100%",
                    background: theme.customColors.grey[300],
                    position: "absolute",
                    right: 0,
                    top: "5px",
                  },
                }}
              >
                <ChartContainer
                  chartData={overViewParticipantsDataForChart}
                  title={formatMessage({
                    id: "communityAnalysis.chart.newParticipants",
                  })}
                  chartStyle="pie"
                  isLoading={overViewParticipantsDataLoading}
                />
              </Box>
              <Box component={Grid} item xs={12} lg={9}>
                <Box pl="16px">
                  <ChartContainer
                    chartData={overViewParticipantsGrowthForChart}
                    chartStyle="bar"
                    title={formatMessage({
                      id: "communityAnalysis.chart.newParticipantGrowth",
                    })}
                    isLoading={overViewParticipantsDataLoading}
                  />
                </Box>
              </Box>
            </Box>
          </StyledWhiteContainer>
        </Box>
        <Grid item xs={12}>
          <StyledWhiteContainer>
            <ChartContainer
              chartData={engagementGrowthDataForChart}
              tooltipText={formatMessage({
                id: "chart.engagementGrowth.tooltip",
              })}
              chartStyle="bar"
              title={formatMessage({
                id: "communityAnalysis.chart.engagementGrowth",
              })}
              isLoading={engagementGrowthDataLoading}
            />
          </StyledWhiteContainer>
        </Grid>
        <Grid item xs={12}>
          <StyledWhiteContainer>
            <Box display={"flex"} alignItems={"center"} mb="12px">
              <Box sx={{ fontSize: "16px" }}>
                {formatMessage({
                  id: "communityAnalysis.chart.retentionCohort",
                })}
              </Box>

              <Box width={"160px"} ml="auto">
                <AutoCompleteDropdown
                  value={timeFrameOptions?.find(
                    (option) => option.value === timeFrame
                  )}
                  placeholder={formatMessage({ id: "menu.placeholder.select" })}
                  options={timeFrameOptions}
                  onChange={handleChangeTimeFrame}
                  name="utmId"
                  size={SIZE.MEDIUM}
                />
              </Box>
            </Box>
            <Box component={Grid} container>
              <Box
                component={Grid}
                item
                xs={12}
                lg={6}
                sx={{
                  position: "relative",
                  "&:after": {
                    content: '""',
                    display: { xs: "none", lg: "block" },
                    width: "1px",
                    height: "100%",
                    background: theme.customColors.grey[300],
                    position: "absolute",
                    right: 0,
                    top: 0,
                  },
                }}
              >
                <Box padding="12px">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "20px",
                      color: theme.customColors.grey[600],
                    }}
                  >
                    <Box
                      component="i"
                      color="#5865F2"
                      className="meta-crm-icon-ic_discord font-size-18"
                      mr="5px"
                    />
                    {formatMessage({ id: "global.commonWord.discord" })}
                  </Box>
                  <HeatContainerForOverView
                    type="discord"
                    color={discordColor}
                    context={CommunityAnalysisContext}
                  ></HeatContainerForOverView>
                </Box>
              </Box>
              <Box component={Grid} item xs={12} lg={6}>
                <Box padding="12px">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "20px",
                      color: theme.customColors.grey[600],
                    }}
                  >
                    <Box
                      component="i"
                      color="#25A3E2"
                      className="meta-crm-icon-ic_telegram font-size-12"
                      mr="5px"
                    />
                    {formatMessage({ id: "global.commonWord.telegram" })}
                  </Box>
                  <Box>
                    <HeatContainerForOverView
                      type="telegram"
                      color={telegramColor}
                      context={CommunityAnalysisContext}
                    ></HeatContainerForOverView>
                  </Box>
                </Box>
              </Box>
            </Box>
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12}>
          <StyledWhiteContainer>
            <ChartContainer
              chartData={overViewKeywordCloudData}
              isLoading={overViewKeywordCloudDataLoading}
              chartStyle="tagCloud"
              title={formatMessage({
                id: "communityAnalysis.chart.keywordCloud",
              })}
            />
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12}>
          <StyledWhiteContainer>
            <ChartContainer
              title={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {formatMessage({
                    id: "communityAnalysis.chart.keywordTrend",
                  })}
                  <Box sx={{ mx: "5px" }}>:</Box>
                  <Box
                    sx={{
                      color: `${theme.customColors.purple[500]}`,
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    {targetKeyword.label}
                  </Box>
                </Box>
              }
              chartStyle="line"
              chartData={overViewKeywordTrendDataForChart}
              isLoading={overViewKeywordTrendDataLoading}
            >
              <Box minWidth={"180px"} ml="auto">
                <AutoCompleteDropdown
                  value={targetKeyword}
                  placeholder={formatMessage({ id: "menu.placeholder.select" })}
                  options={keywordCloudOptions}
                  onChange={handleTargetKeyword}
                  size={SIZE.MEDIUM}
                />
              </Box>
            </ChartContainer>
          </StyledWhiteContainer>
        </Grid>
      </Box>
    </Box>
  );
};

export default OverviewContainer;
